import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Eye, Download, Share2, BookOpen, AlertTriangle, ShieldAlert, ShieldCheck,
  Zap, FileText, Upload, Building2, Sparkles, Scale, BadgeCheck, Briefcase,
  Loader2, ChevronDown, ChevronRight, X, Send, Check, ArrowRight, MoreVertical,
  TrendingUp, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { fmtMoney, DEFAULT_CURRENCY } from "../lib/money";
import { Button } from "../components/ui/button";
import { ScrollPanel } from "../components/ui/scroll-panel";
import { Switch } from "../components/ui/switch";

const LUCIDE_MAP = {
  ShieldAlert, ShieldCheck, Zap, FileText, Upload, Building2, Sparkles,
  Scale, BadgeCheck, Briefcase, AlertTriangle, TrendingUp,
};
function Lucide({ name, size = 16, className = "", strokeWidth = 1.75 }) {
  const Icon = LUCIDE_MAP[name] || FileText;
  return <Icon size={size} className={className} strokeWidth={strokeWidth} />;
}

/* ========================================================================
   QENVA · Rapport Guardian™ Exécutif
   Highest tier — strategic cockpit. Aggregates Dashboard + Sous Vigilance +
   CrossCheck + ALM + Snapshots + Decision Engine into a single decision-grade
   page tailored for CEOs, banks and investors.
======================================================================== */
export default function ExecutiveReport() {
  const { t, i18n } = useTranslation("executive_report");
  const navigate = useNavigate();
  const locale = (i18n.language || "fr").startsWith("fr") ? "fr" : "en";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      const r = await api.get("/executive-report/overview", { params: { locale } });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [locale]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const r = await api.post("/executive-report/preview-html", null, { params: { locale } });
      setPreviewHtml(r.data.html);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await api.post("/exec-report", { template: "ceo" }, { responseType: "blob" });
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `QENVA-Rapport-${data?.workspace?.name || "Executive"}.pdf`;
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const toggleAudience = async (key) => {
    if (!data) return;
    const currently = data.audiences.items.filter((a) => a.enabled).map((a) => a.key);
    const next = currently.includes(key)
      ? currently.filter((k) => k !== key)
      : [...currently, key];
    try {
      const r = await api.patch("/executive-report/audiences", { enabled: next }, { params: { locale } });
      setData({ ...data, audiences: r.data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading) return <PageLoader />;
  if (!data) return null;

  const currency = data.currency || DEFAULT_CURRENCY;

  return (
    <div className="animate-fade-up" data-testid="executive-report-page">
      {/* Top utility bar */}
      <TopBar
        breadcrumb={t("breadcrumb")}
        onPreview={openPreview}
        onPdf={downloadPdf}
        onShare={() => setShareOpen(true)}
        pdfLoading={pdfLoading}
      />

      {/* Header méta */}
      <Header data={data} locale={locale} />

      {/* Section 1 — Cockpit */}
      <CockpitHero data={data} currency={currency} />

      {/* Section 2 — Guardian Decision */}
      <GuardianDecisionCard
        decision={data.guardian_decision}
        currency={currency}
        onAct={() => navigate(data.guardian_decision?.cta_route || "/alerts")}
      />

      {/* Section 3 — Plan d'actions */}
      <ActionPlan data={data.action_plan} onAction={(r) => navigate(r)} />

      {/* Risks ↔ Opportunities side-by-side (desktop) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10" data-testid="risk-opp-grid">
        <RisksSection data={data.risks} currency={currency} onAction={(r) => navigate(r)} />
        <OpportunitiesSection data={data.opportunities} currency={currency} onAction={(r) => navigate(r)} />
      </div>

      {/* Section 5 — Échéancier */}
      <DeadlinesSection data={data.deadlines} />

      {/* Section 6 — Santé Entreprise™ */}
      <HealthByDomain data={data.health_by_domain} />

      {/* Section 7 — Destinataires */}
      <AudienceList items={data.audiences.items} onToggle={toggleAudience} />

      {/* Modals */}
      {previewOpen && (
        <PreviewModal
          html={previewHtml}
          loading={previewLoading}
          onClose={() => setPreviewOpen(false)}
        />
      )}
      {shareOpen && (
        <ShareDialog
          audiences={data.audiences.items}
          locale={locale}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}

/* ----------------------------- Top Bar ---------------------------------- */
function TopBar({ breadcrumb, onPreview, onPdf, onShare, pdfLoading }) {
  const { t } = useTranslation("executive_report");
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
      <p className="text-[13px] text-zinc-500" data-testid="exec-breadcrumb">{breadcrumb}</p>
      {/* Desktop actions */}
      <div className="hidden sm:flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onPreview} data-testid="exec-preview-btn">
          <Eye size={14} className="mr-1.5" /> {t("actions.preview")}
        </Button>
        <Button variant="outline" size="sm" onClick={onPdf} disabled={pdfLoading} data-testid="exec-pdf-btn">
          {pdfLoading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
          {t("actions.pdf")}
        </Button>
        <Button size="sm" onClick={onShare} className="bg-qenva-blue hover:bg-qenva-blue/90" data-testid="exec-share-btn">
          <Share2 size={14} className="mr-1.5" /> {t("actions.share")}
        </Button>
      </div>
      {/* Mobile actions */}
      <div className="sm:hidden flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onPreview} data-testid="exec-preview-btn-mobile">
          <Eye size={14} className="mr-1.5" /> {t("actions.preview")}
        </Button>
        <div className="relative">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-zinc-200 bg-white"
            data-testid="exec-more-menu"
          >
            <MoreVertical size={14} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-44 rounded-xl border border-zinc-200 bg-white shadow-lg overflow-hidden z-30">
              <button onClick={onPdf} className="flex items-center gap-2 w-full px-3 py-2 text-[13px] hover:bg-zinc-50">
                <Download size={13} /> {t("actions.pdf")}
              </button>
              <button onClick={onShare} className="flex items-center gap-2 w-full px-3 py-2 text-[13px] hover:bg-zinc-50">
                <Share2 size={13} /> {t("actions.share")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Header --------------------------------- */
function Header({ data, locale }) {
  const { t } = useTranslation("executive_report");
  const generatedDate = useMemo(() => {
    try {
      const d = new Date(data.generated_at);
      return d.toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", {
        day: "numeric", month: "long", year: "numeric",
      });
    } catch { return ""; }
  }, [data.generated_at, locale]);

  return (
    <div className="mb-7" data-testid="exec-header">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-qenva-blue inline-flex items-center gap-1.5">
          <BookOpen size={12} strokeWidth={1.75} />
          QENVA · {t("brand_tag")}
        </p>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 text-[10.5px] font-bold uppercase tracking-wider">
          <Lock size={9} /> {t("confidential")}
        </span>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-qenva-navy">
        {t("page_title")}
      </h1>
      <p className="text-[15px] text-zinc-500 mt-2">
        {t("page_subtitle", { workspace: data.workspace.name })}
      </p>
      <div className="flex items-center flex-wrap gap-x-5 gap-y-2 mt-4 text-[12.5px] text-zinc-500">
        <span>📅 {t("generated_on", { date: generatedDate })}</span>
        <span>· {t("period_label", { period: data.period.label })}</span>
        <span>· {t("recipients_label")}</span>
        {data.audiences.items.filter((a) => a.enabled).map((a) => (
          <AudienceChip key={a.key} item={a} small />
        ))}
      </div>
    </div>
  );
}

/* ----------------------------- Cockpit Hero --------------------------- */
function CockpitHero({ data, currency }) {
  const { t } = useTranslation("executive_report");
  const c = data.cockpit;
  const narrative = data.narrative;
  return (
    <section
      className="rounded-3xl bg-qenva-navy text-white p-6 sm:p-8 relative overflow-hidden"
      data-testid="cockpit-hero"
    >
      {/* Subtle emerald halo */}
      <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-qenva-blue/20 blur-3xl" />

      <div className="relative">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-400 inline-flex items-center gap-1.5">
            <Sparkles size={12} /> {t("cockpit.title")}
          </p>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 text-[10.5px] font-bold uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {t("online")}
          </span>
        </div>
        <p className="text-[12.5px] text-white/50 italic mb-7">« {t("cockpit.question")} »</p>

        {/* Donuts + status + KPIs in one responsive grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-7 items-center">
          {/* Donuts */}
          <div className="flex items-center gap-4 sm:gap-6">
            <DonutGauge value={c.guardian_score} color="#10B981" trackColor="#1E40AF40" size={120} label={t("cockpit.guardian")} />
            <DonutGauge value={c.crosscheck_score} color="#10B981" trackColor="#FFFFFF20" size={104} label={t("cockpit.crosscheck")} />
            <div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[12px] font-semibold">
                <Check size={12} /> {c.status_label}
              </span>
              <p className="text-[11.5px] text-white/60 mt-2">{data.period.label}</p>
            </div>
          </div>

          {/* KPIs band */}
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 bg-white/5 ring-1 ring-white/10 rounded-2xl px-4 py-4"
            data-testid="cockpit-kpis"
          >
            <KpiInline value={fmtMoney(c.kpis.value_under_watch || 0, currency)} label={t("cockpit.sous_vigilance")} color="text-blue-300" testId="kpi-under-watch" />
            <KpiInline value={fmtMoney(c.kpis.value_preserved || 0, currency)} label={t("cockpit.preservee")} color="text-emerald-300" testId="kpi-preserved" />
            <KpiInline value={fmtMoney(c.kpis.value_preservable || 0, currency)} label={t("cockpit.preservable")} color="text-amber-300" testId="kpi-preservable" />
            <KpiInline value={c.kpis.active_risks} label={t("cockpit.risks_active")} color="text-rose-300" testId="kpi-active-risks" />
            <KpiInline value={c.kpis.documents_monitored} label={t("cockpit.docs")} color="text-white" testId="kpi-docs" />
          </div>
        </div>

        {/* Narrative banner */}
        <div
          className="mt-6 px-5 py-4 rounded-2xl bg-white/5 ring-1 ring-emerald-500/30 backdrop-blur-sm"
          data-testid="cockpit-narrative"
        >
          <p className="text-[13.5px] sm:text-[14.5px] leading-relaxed text-white/90">
            <span className="text-emerald-400 mr-1.5">✦</span>
            {narrative?.sentence}
          </p>
        </div>
      </div>
    </section>
  );
}

function DonutGauge({ value, color, trackColor, size = 100, label }) {
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - (value / 100));
  return (
    <div className="relative inline-flex items-center justify-center" data-testid="donut-gauge">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke={trackColor} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={color} strokeWidth={stroke} strokeLinecap="round" fill="none"
          strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 600ms ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-[28px] sm:text-[32px] font-bold text-white tabular-nums leading-none">{value}</span>
        <span className="text-[10px] text-white/60 mt-1">{label}</span>
      </div>
    </div>
  );
}

function KpiInline({ value, label, color, testId }) {
  return (
    <div data-testid={testId}>
      <p className={`font-display text-[19px] sm:text-[22px] font-bold tabular-nums leading-none ${color}`}>
        {value}
      </p>
      <p className="text-[10.5px] uppercase tracking-wider text-white/60 mt-1.5 font-semibold">
        {label}
      </p>
    </div>
  );
}

/* ---------------------- Guardian Decision (premium) ------------------- */
function GuardianDecisionCard({ decision, currency, onAct }) {
  const { t } = useTranslation("executive_report");
  if (!decision) return null;
  const confidence = decision.confidence || 0;
  const impact = decision.impact_estimated || 0;

  return (
    <section
      className="mt-8 rounded-3xl border border-emerald-200 bg-white p-6 sm:p-7 relative overflow-hidden shadow-[0_8px_30px_-12px_rgba(16,185,129,0.25)]"
      data-testid="guardian-decision-card"
    >
      <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-emerald-100/60 blur-3xl" />
      <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-start">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-600 inline-flex items-center gap-1.5">
              <Sparkles size={12} /> {t("decision.label")}
            </p>
          </div>
          <p className="text-[12px] text-zinc-400 italic mb-3">« {t("decision.question")} »</p>
          <h3 className="font-display text-[22px] sm:text-[24px] font-semibold text-qenva-navy leading-tight">
            {decision.decision_recommended}
          </h3>
          <p className="text-[13.5px] text-zinc-600 leading-relaxed mt-3">
            <span className="text-zinc-400 font-semibold mr-1">{t("decision.why_label")} :</span>
            {decision.rationale}
          </p>

          <div className="grid grid-cols-2 gap-4 mt-5">
            <div>
              <p className="text-[10.5px] uppercase tracking-wider font-bold text-zinc-400">{t("decision.impact_label")}</p>
              <p className="font-display text-[22px] font-bold text-emerald-600 tabular-nums mt-1" data-testid="decision-impact">
                {impact > 0 ? fmtMoney(impact, currency) : "—"}
              </p>
            </div>
            <div>
              <p className="text-[10.5px] uppercase tracking-wider font-bold text-zinc-400">{t("decision.confidence_label")}</p>
              <div className="flex items-center gap-2 mt-1">
                <ConfidenceRing value={confidence} />
                <p className="font-display text-[22px] font-bold text-qenva-navy tabular-nums" data-testid="decision-confidence">{confidence}%</p>
              </div>
            </div>
          </div>
        </div>

        <Button
          size="lg"
          onClick={onAct}
          className="bg-qenva-navy hover:bg-qenva-navy/90 text-white px-6"
          data-testid="decision-cta"
        >
          {decision.cta_label || t("decision.cta_default")}
          <ArrowRight size={16} className="ml-1.5" />
        </Button>
      </div>
    </section>
  );
}

function ConfidenceRing({ value }) {
  const size = 44, stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - (value / 100));
  const color = value >= 80 ? "#10B981" : value >= 60 ? "#3B82F6" : "#F59E0B";
  return (
    <svg width={size} height={size} className="-rotate-90 shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#E4E4E7" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        stroke={color} strokeWidth={stroke} strokeLinecap="round" fill="none"
        strokeDasharray={c} strokeDashoffset={offset}
      />
    </svg>
  );
}

/* ----------------------- Action Plan timeline ------------------------- */
function ActionPlan({ data, onAction }) {
  const { t } = useTranslation("executive_report");
  if (!data) return null;
  const groups = [
    { key: "today", color: "rose", icon: "AlertTriangle" },
    { key: "this_month", color: "amber", icon: "FileText" },
    { key: "watch", color: "blue", icon: "Eye" },
  ];
  let counter = 0;
  return (
    <section className="mt-10" data-testid="section-action-plan">
      <SectionHeader
        title={t("action_plan.title")}
        question={t("action_plan.question")}
        subtitle={t("action_plan.subtitle", { count: data.total, urgent: data.urgent_count })}
        rightBadge={data.urgent_count > 0 && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 text-rose-600 text-[11.5px] font-bold">
            <AlertTriangle size={11} />
            {t("action_plan.urgent_badge", { count: data.urgent_count })}
          </span>
        )}
      />
      {data.total === 0 ? (
        <EmptyState label={t("action_plan.empty")} />
      ) : (
        <div className="rounded-2xl bg-white border border-zinc-200 overflow-hidden divide-y divide-zinc-100">
          {groups.map((g) => {
            const rows = data.buckets[g.key] || [];
            if (rows.length === 0) return null;
            const colorMap = {
              rose: { bg: "bg-rose-50", text: "text-rose-600", dot: "bg-rose-500" },
              amber: { bg: "bg-amber-50", text: "text-amber-600", dot: "bg-amber-500" },
              blue: { bg: "bg-qenva-blue/10", text: "text-qenva-blue", dot: "bg-qenva-blue" },
            }[g.color];
            return (
              <div key={g.key}>
                <div className={`${colorMap.bg} px-5 py-2 flex items-center gap-2`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${colorMap.dot}`} />
                  <span className={`text-[11px] uppercase tracking-[0.16em] font-bold ${colorMap.text}`}>
                    {t(`action_plan.buckets.${g.key}`)}
                  </span>
                </div>
                <ul>
                  {rows.map((row) => {
                    counter += 1;
                    return (
                      <ActionRow
                        key={row.id}
                        index={counter}
                        row={row}
                        color={g.color}
                        onClick={() => onAction(row.cta_route)}
                      />
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ActionRow({ index, row, color, onClick }) {
  const colorMap = {
    rose: { btnBg: "bg-rose-500", btnHover: "hover:bg-rose-600", iconBg: "bg-rose-50", iconColor: "text-rose-500" },
    amber: { btnBg: "bg-amber-100 text-amber-700", btnHover: "hover:bg-amber-200", iconBg: "bg-amber-50", iconColor: "text-amber-600" },
    blue: { btnBg: "bg-qenva-blue/10 text-qenva-blue", btnHover: "hover:bg-qenva-blue/20", iconBg: "bg-qenva-blue/10", iconColor: "text-qenva-blue" },
  }[color];
  const isCritical = color === "rose";
  return (
    <li
      className="flex items-center gap-4 px-5 py-4 hover:bg-zinc-50/60 transition-colors"
      data-testid={`action-row-${row.id}`}
    >
      <span className={`font-display text-[14px] font-bold ${colorMap.iconColor} tabular-nums w-6 shrink-0`}>
        {index}
      </span>
      <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${colorMap.iconBg} ${colorMap.iconColor}`}>
        <Lucide name={row.icon} size={15} />
      </div>
      <p className="text-[14px] text-qenva-navy flex-1 min-w-0">{row.title}</p>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 px-4 py-2 rounded-full text-[12.5px] font-semibold transition-colors ${
          isCritical ? `text-white ${colorMap.btnBg} ${colorMap.btnHover}` : `${colorMap.btnBg} ${colorMap.btnHover}`
        }`}
        data-testid={`action-cta-${row.id}`}
      >
        {row.cta_label}
      </button>
    </li>
  );
}

/* --------------------------- Risks Section --------------------------- */
function RisksSection({ data, currency, onAction }) {
  const { t } = useTranslation("executive_report");
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  if (!data) return null;
  return (
    <section data-testid="section-risks">
      <SectionHeader
        title={t("risks.title")}
        question={t("risks.question")}
        subtitle={t("risks.subtitle", { count: data.total, blocking: data.blocking_count })}
        rightBadge={data.blocking_count > 0 && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 text-rose-600 text-[11.5px] font-bold">
            <AlertTriangle size={11} />
            {t("risks.blocking_badge", { count: data.blocking_count })}
          </span>
        )}
        rightLink={{ label: "CrossCheck™ →", route: "/crosscheck" }}
      />
      {data.critical.length === 0 ? (
        <EmptyState label={t("risks.empty")} tone="emerald" />
      ) : (
        <div className="space-y-3">
          {data.critical.map((r) => <RiskCard key={r.id} risk={r} currency={currency} onAction={() => onAction(r.cta_route)} />)}
        </div>
      )}
      {data.secondary.length > 0 && (
        <button
          type="button"
          onClick={() => setSecondaryOpen((o) => !o)}
          className="mt-4 w-full px-5 py-3 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-between border border-amber-200/70 hover:bg-amber-100/70 transition-colors"
          data-testid="risks-secondary-toggle"
        >
          <span className="inline-flex items-center gap-2 text-[13px] font-semibold">
            <AlertTriangle size={13} />
            <span className="px-1.5 py-0.5 rounded bg-amber-200/60 text-[11px]">
              {data.secondary.length} {t("risks.secondary_label")}
            </span>
            {t("risks.secondary_title")}
          </span>
          <ChevronDown size={16} className={`transition-transform ${secondaryOpen ? "rotate-180" : ""}`} />
        </button>
      )}
      {secondaryOpen && (
        <div className="mt-3 space-y-3" data-testid="risks-secondary-list">
          {data.secondary.map((r) => <RiskCard key={r.id} risk={r} currency={currency} onAction={() => onAction(r.cta_route)} compact />)}
        </div>
      )}
    </section>
  );
}

function RiskCard({ risk, currency, onAction, compact }) {
  const { t } = useTranslation("executive_report");
  const fr = useTranslation().i18n.language.startsWith("fr");
  const isCritical = risk.severity === "Critical";
  const borderColor = isCritical ? "border-l-rose-500" : "border-l-amber-500";
  const badgeColor = isCritical ? "bg-rose-50 text-rose-600 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200";
  const impactColor = isCritical ? "text-rose-500" : "text-amber-600";
  return (
    <div
      className={`rounded-2xl bg-white border border-zinc-200 border-l-4 ${borderColor} p-5 ${compact ? "py-4" : ""}`}
      data-testid={`risk-card-${risk.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${badgeColor}`}>
              <AlertTriangle size={10} /> {risk.severity_label}
            </span>
          </div>
          <p className="font-display text-[15.5px] font-semibold text-qenva-navy">{risk.title}</p>
          {risk.recommendation && !compact && (
            <p className={`text-[12.5px] ${impactColor} mt-2`}>→ {risk.recommendation}</p>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">{t("risks.labels.impact")}</p>
            <p className={`font-display text-[16px] font-bold ${impactColor} tabular-nums`}>
              {fmtMoney(risk.impact || 0, currency)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">{t("risks.labels.deadline")}</p>
            <p className="text-[13px] font-semibold text-qenva-navy tabular-nums">
              {risk.due_at ? new Date(risk.due_at).toLocaleDateString(fr ? "fr-FR" : "en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={onAction}
            className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-[12px] font-semibold ${
              isCritical ? "bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200" : "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
            }`}
            data-testid={`risk-cta-${risk.id}`}
          >
            {risk.cta_label}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------- Opportunities Section ------------------------ */
function OpportunitiesSection({ data, currency, onAction }) {
  const { t } = useTranslation("executive_report");
  if (!data) return null;
  return (
    <section data-testid="section-opportunities">
      <SectionHeader
        title={t("opportunities.title")}
        question={t("opportunities.question")}
        subtitle={t("opportunities.subtitle", { count: data.count, value: fmtMoney(data.preservable_annual || 0, currency) + "/an" })}
        rightBadge={data.preservable_annual > 0 && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[11.5px] font-bold">
            ↗ {t("opportunities.annual_badge", { value: fmtMoney(data.preservable_annual, currency) })}
          </span>
        )}
      />
      {data.items.length === 0 ? (
        <EmptyState label={t("opportunities.empty")} tone="emerald" />
      ) : (
        <div className="space-y-3">
          {data.items.map((o) => <OpportunityCard key={o.id} opp={o} currency={currency} onAction={() => onAction(o.cta_route)} />)}
        </div>
      )}
    </section>
  );
}

function OpportunityCard({ opp, currency, onAction }) {
  const { t } = useTranslation("executive_report");
  const COLOR_MAP = {
    emerald: { iconBg: "bg-emerald-50", iconColor: "text-emerald-600", value: "text-emerald-600", barFill: "bg-emerald-500", btn: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200" },
    blue: { iconBg: "bg-qenva-blue/10", iconColor: "text-qenva-blue", value: "text-qenva-blue", barFill: "bg-qenva-blue", btn: "bg-qenva-blue/10 text-qenva-blue hover:bg-qenva-blue/20 border-qenva-blue/30" },
    violet: { iconBg: "bg-violet-50", iconColor: "text-violet-600", value: "text-violet-600", barFill: "bg-violet-500", btn: "bg-violet-50 text-violet-700 hover:bg-violet-100 border-violet-200" },
    amber: { iconBg: "bg-amber-50", iconColor: "text-amber-600", value: "text-amber-600", barFill: "bg-amber-500", btn: "bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200" },
  };
  const c = COLOR_MAP[opp.color] || COLOR_MAP.emerald;
  return (
    <div className="rounded-2xl bg-white border border-zinc-200 p-5" data-testid={`opportunity-card-${opp.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${c.iconBg} ${c.iconColor}`}>
            <Lucide name={opp.icon} size={16} />
          </div>
          <p className="font-display text-[15.5px] font-semibold text-qenva-navy">{opp.title}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">{t("opportunities.value_preserved_label")}</p>
          <p className={`font-display text-[18px] font-bold ${c.value} tabular-nums`}>
            {fmtMoney(opp.annual_value || 0, currency)}<span className="text-[11px] font-normal text-zinc-400">/an</span>
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 mt-3">
        <div className="flex-1 min-w-0 max-w-[220px]">
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1">{t("opportunities.confidence_label")}</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
              <div className={`h-full ${c.barFill} transition-all`} style={{ width: `${opp.confidence}%` }} />
            </div>
            <span className={`text-[12.5px] font-bold ${c.value} tabular-nums w-9 text-right`}>{opp.confidence}%</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onAction}
          className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-[12px] font-semibold border ${c.btn}`}
          data-testid={`opportunity-cta-${opp.id}`}
        >
          {opp.cta_label}
        </button>
      </div>
    </div>
  );
}

/* --------------------------- Deadlines ------------------------------- */
function DeadlinesSection({ data }) {
  const { t, i18n } = useTranslation("executive_report");
  const [extraOpen, setExtraOpen] = useState(false);
  const fr = i18n.language.startsWith("fr");
  if (!data) return null;
  const total = (data.j30?.length || 0) + (data.j60?.length || 0) + (data.j90?.length || 0);
  const extraCount = (data.j60?.length || 0) + (data.j90?.length || 0);
  return (
    <section className="mt-10" data-testid="section-deadlines">
      <SectionHeader
        title={t("deadlines.title")}
        question={t("deadlines.question")}
        subtitle={t("deadlines.subtitle")}
      />
      {total === 0 ? (
        <EmptyState label={t("deadlines.empty")} tone="emerald" />
      ) : (
        <div className="rounded-2xl bg-white border border-zinc-200 overflow-hidden">
          {data.j30?.length > 0 && (
            <>
              <div className="px-5 py-2 bg-rose-50 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-rose-600">{t("deadlines.j30")}</span>
              </div>
              <ul className="divide-y divide-zinc-100">
                {data.j30.map((d) => <DeadlineRow key={d.id} row={d} urgent fr={fr} />)}
              </ul>
            </>
          )}
          {extraCount > 0 && (
            <button
              type="button"
              onClick={() => setExtraOpen((o) => !o)}
              className="w-full px-5 py-3 text-[13px] text-zinc-500 hover:bg-zinc-50/70 inline-flex items-center justify-between"
              data-testid="deadlines-extra-toggle"
            >
              <span>{t("deadlines.see_more", { count: extraCount })}</span>
              <ChevronDown size={15} className={`transition-transform ${extraOpen ? "rotate-180" : ""}`} />
            </button>
          )}
          {extraOpen && (
            <>
              {data.j60?.length > 0 && (
                <>
                  <div className="px-5 py-2 bg-amber-50 flex items-center gap-2 border-t border-zinc-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-amber-600">{t("deadlines.j60")}</span>
                  </div>
                  <ul className="divide-y divide-zinc-100">
                    {data.j60.map((d) => <DeadlineRow key={d.id} row={d} fr={fr} />)}
                  </ul>
                </>
              )}
              {data.j90?.length > 0 && (
                <>
                  <div className="px-5 py-2 bg-qenva-blue/10 flex items-center gap-2 border-t border-zinc-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-qenva-blue" />
                    <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-qenva-blue">{t("deadlines.j90")}</span>
                  </div>
                  <ul className="divide-y divide-zinc-100">
                    {data.j90.map((d) => <DeadlineRow key={d.id} row={d} fr={fr} />)}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function DeadlineRow({ row, urgent, fr }) {
  const iconBg = urgent ? "bg-rose-50 text-rose-500" : "bg-zinc-100 text-zinc-500";
  const dateColor = urgent ? "text-rose-500" : "text-zinc-700";
  const date = row.due_at ? new Date(row.due_at).toLocaleDateString(fr ? "fr-FR" : "en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
  return (
    <li className="flex items-center gap-4 px-5 py-3.5" data-testid={`deadline-row-${row.id}`}>
      <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${iconBg}`}>
        <Lucide name={row.icon} size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-qenva-navy">{row.title}</p>
        {row.subtitle && <p className="text-[12px] text-zinc-500 truncate">{row.subtitle}</p>}
      </div>
      <p className={`text-[13px] font-semibold tabular-nums ${dateColor} shrink-0`}>{date}</p>
    </li>
  );
}

/* ----------------------- Health by Domain ---------------------------- */
function HealthByDomain({ data }) {
  const { t } = useTranslation("executive_report");
  if (!data) return null;
  const COLOR_BAR = {
    emerald: "bg-emerald-500", blue: "bg-qenva-blue",
    amber: "bg-amber-500", rose: "bg-rose-500", zinc: "bg-zinc-300",
  };
  const COLOR_TEXT = {
    emerald: "text-emerald-600", blue: "text-qenva-blue",
    amber: "text-amber-600", rose: "text-rose-500", zinc: "text-zinc-400",
  };
  return (
    <section className="mt-10" data-testid="section-health">
      <SectionHeader
        title={t("health.title")}
        question={t("health.question")}
        subtitle={t("health.subtitle")}
      />
      <div className="rounded-2xl bg-white border border-zinc-200 p-6">
        <ul className="space-y-4">
          {data.domains.map((d) => (
            <li key={d.domain} className="grid grid-cols-12 gap-4 items-center" data-testid={`health-${d.domain}`}>
              <div className="col-span-3 sm:col-span-2">
                <p className="text-[13.5px] font-semibold text-qenva-navy">{d.domain}</p>
                <p className="text-[10.5px] text-zinc-400">{d.doc_count} docs</p>
              </div>
              <div className="col-span-7 sm:col-span-9">
                <div className="h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${COLOR_BAR[d.color] || "bg-zinc-300"}`}
                    style={{ width: `${d.score || 0}%` }}
                  />
                </div>
              </div>
              <div className="col-span-2 sm:col-span-1 text-right">
                <span className={`font-display text-[16px] font-bold tabular-nums ${COLOR_TEXT[d.color] || "text-zinc-400"}`}>
                  {d.unmeasured ? "—" : d.score}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------------------------- Audiences ------------------------------ */
function AudienceList({ items, onToggle }) {
  const { t } = useTranslation("executive_report");
  return (
    <section className="mt-10" data-testid="section-audiences">
      <SectionHeader
        title={t("audiences.title")}
        subtitle={t("audiences.subtitle")}
      />
      <div className="flex flex-wrap gap-3">
        {items.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => onToggle(a.key)}
            data-testid={`audience-chip-${a.key}`}
          >
            <AudienceChip item={a} interactive />
          </button>
        ))}
      </div>
    </section>
  );
}

function AudienceChip({ item, small, interactive }) {
  const COLOR_MAP = {
    blue: { bg: "bg-qenva-blue/10", text: "text-qenva-blue", ring: "ring-qenva-blue/30" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
    violet: { bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200" },
    amber: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200" },
    zinc: { bg: "bg-zinc-100", text: "text-zinc-700", ring: "ring-zinc-200" },
  };
  const c = COLOR_MAP[item.color] || COLOR_MAP.zinc;
  const size = small ? "px-2 py-0.5 text-[11px]" : "px-3 py-1.5 text-[12.5px]";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ring-1 font-semibold ${size} ${c.bg} ${c.text} ${c.ring} ${
        !item.enabled ? "opacity-40 line-through" : ""
      } ${interactive ? "cursor-pointer hover:scale-[1.02] transition-transform" : ""}`}
    >
      {item.label}
      {!small && item.enabled && <Check size={11} />}
    </span>
  );
}

/* ------------------------ Section Header & Empty --------------------- */
function SectionHeader({ title, question, subtitle, rightBadge, rightLink }) {
  return (
    <div className="flex items-end justify-between gap-3 flex-wrap mb-5">
      <div className="min-w-0">
        <h2 className="font-display text-[22px] sm:text-[24px] font-semibold text-qenva-navy">{title}</h2>
        {question && <p className="text-[12px] text-zinc-400 italic mt-0.5">« {question} »</p>}
        {subtitle && <p className="text-[13px] text-zinc-500 mt-1.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-3">
        {rightBadge}
        {rightLink && (
          <a href={rightLink.route} className="text-[12.5px] font-semibold text-qenva-blue hover:underline">
            {rightLink.label}
          </a>
        )}
      </div>
    </div>
  );
}

function EmptyState({ label, tone = "zinc" }) {
  const colors = tone === "emerald"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-zinc-50 text-zinc-500 border-zinc-200";
  return (
    <div className={`rounded-2xl border ${colors} p-6 text-center text-[13px]`} data-testid="empty-state">
      {label}
    </div>
  );
}

function PageLoader() {
  return (
    <div className="grid place-items-center h-[60vh] text-zinc-400">
      <Loader2 className="w-5 h-5 animate-spin" />
    </div>
  );
}

/* --------------------------- Preview Modal --------------------------- */
function PreviewModal({ html, loading, onClose }) {
  const { t } = useTranslation("executive_report");
  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="exec-preview-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between bg-zinc-50 shrink-0">
          <p className="text-[14px] font-semibold text-qenva-navy">{t("preview.title")}</p>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100" data-testid="exec-preview-close">
            <X size={16} />
          </button>
        </div>
        <ScrollPanel className="bg-zinc-100">
          {loading ? (
            <div className="p-10 grid place-items-center text-zinc-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <iframe
              srcDoc={html}
              className="w-full h-[80vh]"
              title="Executive report preview"
              sandbox="allow-same-origin"
              data-testid="exec-preview-iframe"
            />
          )}
        </ScrollPanel>
      </div>
    </div>
  );
}

/* --------------------------- Share Dialog ---------------------------- */
function ShareDialog({ audiences, locale, onClose }) {
  const { t } = useTranslation("executive_report");
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [picked, setPicked] = useState(audiences.filter((a) => a.enabled).map((a) => a.key));

  const submit = async () => {
    const recipients = emails.split(/[,\s]+/).map((e) => e.trim()).filter(Boolean);
    if (recipients.length === 0) {
      toast.error("Au moins un destinataire requis");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/executive-report/share", {
        recipient_emails: recipients, audiences: picked, note,
      }, { params: { locale } });
      const statuses = r.data.results || [];
      const sent = statuses.filter((x) => x.status === "sent").length;
      const skipped = statuses.filter((x) => x.status === "skipped").length;
      if (sent > 0) toast.success(t("share.sent_ok"));
      else if (skipped > 0) toast.message(t("share.sent_skipped"));
      else toast.error(t("share.sent_failed"));
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("share.sent_failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="exec-share-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <p className="text-[15px] font-semibold text-qenva-navy">{t("share.title")}</p>
            <p className="text-[12px] text-zinc-500 mt-0.5">{t("share.subtitle")}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100" data-testid="exec-share-close">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-zinc-500">{t("share.emails_label")}</label>
            <textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder={t("share.emails_placeholder")}
              rows={2}
              className="w-full mt-1.5 px-3 py-2 text-[13px] rounded-lg border border-zinc-200 outline-none focus:border-qenva-blue/60"
              data-testid="share-emails"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-zinc-500">{t("share.audiences_label")}</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {audiences.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => setPicked((p) => p.includes(a.key) ? p.filter((k) => k !== a.key) : [...p, a.key])}
                  data-testid={`share-audience-${a.key}`}
                >
                  <AudienceChip item={{ ...a, enabled: picked.includes(a.key) }} interactive />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-zinc-500">{t("share.note_label")}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("share.note_placeholder")}
              rows={3}
              className="w-full mt-1.5 px-3 py-2 text-[13px] rounded-lg border border-zinc-200 outline-none focus:border-qenva-blue/60"
              data-testid="share-note"
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-zinc-200 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>{useTranslation().t("common.cancel", "Annuler")}</Button>
          <Button size="sm" onClick={submit} disabled={sending} className="bg-qenva-blue hover:bg-qenva-blue/90" data-testid="share-submit">
            {sending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />}
            {sending ? t("share.sending") : t("share.send")}
          </Button>
        </div>
      </div>
    </div>
  );
}
